<template>
  <div class="flex min-h-full flex-1 flex-col justify-center px-6 pt-24 sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-sm">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mx-auto mt-8 w-full max-w-sm">
      <p class="mb-6 text-center text-sm text-muted-foreground">
        {{ t("msg.serverUrlConnectDescription") }}
      </p>

      <FormKit type="form" @submit="handleConnect" :actions="false" class="space-y-4">
        <FormKit
          type="text"
          :label="t('msg.serverUrl')"
          :placeholder="t('msg.serverUrlPlaceholder')"
          :classes="{ outer: { 'max-w-[20em]': false } }"
          v-model="serverUrlInput"
          name="serverUrl"
          id="serverUrl"
          validation="required"
          inputmode="url"
          autocapitalize="off"
          autocorrect="off"
        />

        <p v-if="status === 'error' && errorReason" class="text-sm text-danger">
          {{ t(errorMessageKey) }}
        </p>

        <button
          type="submit"
          :disabled="status === 'validating' || status === 'updating'"
          :class="globals.btnClass(theme, highAccessibility)"
          class="!mt-6 flex w-full items-center justify-center rounded-md px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          <font-awesome-icon
            v-if="status === 'validating' || status === 'updating'"
            icon="fa-sharp-duotone fa-solid fa-spinner-third"
            spin
            class="mr-2"
          />
          <template v-if="status === 'validating'">{{ t("msg.serverUrlConnecting") }}</template>
          <template v-else-if="status === 'updating'">{{ t("msg.serverUrlUpdating") }}</template>
          <template v-else>{{ t("msg.serverUrlConnect") }}</template>
        </button>
      </FormKit>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { FormKit } from "@formkit/vue";
import { useI18n } from "vue-i18n";

import globals from "../globals";
import WordmarkLogo from "../components/WordmarkLogo.vue";
import { getServerUrl, setServerUrl, probeServerUrl } from "../capacitor/server-url";
import { checkForUpdateAndAwait } from "../capacitor/app-updater";
import { useThemeStore } from "../pinia/theme.ts";

const { t } = useI18n();
const router = useRouter();
const themeStore = useThemeStore();

const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);

const serverUrlInput = ref("");
const status = ref("idle"); // idle | validating | updating | error
const errorReason = ref(null); // null | "invalid-url" | "timeout" | "unreachable" | "not-knocknoc"

const PROBE_ERROR_KEYS = {
  "invalid-url": "msg.serverUrlErrorInvalid",
  timeout: "msg.serverUrlErrorTimeout",
  "not-knocknoc": "msg.serverUrlErrorNotKnocknoc",
  unreachable: "msg.serverUrlErrorUnreachable",
};

const errorMessageKey = computed(() => PROBE_ERROR_KEYS[errorReason.value] ?? PROBE_ERROR_KEYS.unreachable);

async function handleConnect() {
  const url = serverUrlInput.value.trim();
  if (!url) return;

  status.value = "validating";
  errorReason.value = null;

  const probe = await probeServerUrl(url);
  if (!probe.ok) {
    status.value = "error";
    errorReason.value = probe.reason;
    return;
  }

  await setServerUrl(probe.normalizedUrl);

  status.value = "updating";

  try {
    console.log("[Connect] Starting OTA check...");
    const reloading = await checkForUpdateAndAwait();
    console.log("[Connect] OTA result: reloading=" + reloading);

    // If an update was applied, the WebView reloads automatically.
    // After reload, the router guard will find the server URL set and go to login.
    if (!reloading) {
      router.push({ name: "Login" });
    }
  } catch (e) {
    console.log("[Connect] OTA check threw: " + e);
    router.push({ name: "Login" });
  }
}

onMounted(async () => {
  const stored = await getServerUrl();
  if (stored) {
    serverUrlInput.value = stored;
  }
});
</script>
